import { createContext, useContext } from 'react';
import { type ChatInteractionMode } from '@trinity/chat/enums';

/**
 * AvatarPanelContext — bridges the HeyGen avatar panel's expanded state
 * from DigitalTwinScreen down to Input.tsx, without coupling the shared
 * Chat input component to Digital Twin concepts.
 *
 * The default value is a no-op so that any non-DT consumer of Input.tsx
 * is completely unaffected (setExpanded is a no-op, isDigitalTwin is false).
 */
/**
 * The current interaction mode in the Digital Twin chat:
 *  - 'video' (default): avatar session active, user types text, TTS drives avatar speech.
 *  - 'voice': avatar session active, mic input enabled.
 *  - 'text': avatar session terminated, plain chat without TTS.
 */

export type AvatarPanelContextValue = {
  /** Whether the avatar panel is in expanded (full-width) mode. Independent of mode. */
  expanded: boolean;
  /** Toggle the expanded state. */
  setExpanded: (value: boolean) => void;
  /** Whether new chat interactions layout is enabled. */
  isNewChatInteractionLayout: boolean;
  /**
   * True only when rendered inside DigitalTwinChat.
   * Input.tsx uses this to decide whether to show the dropdown instead of
   * the standard voice mode switch.
   */
  isDigitalTwin: boolean;
  /* Default interaction mode */
  defaultInteractionMode?: ChatInteractionMode;
  /* Available interaction modes. If undefined, all modes are available. */
  availableModes?: ChatInteractionMode[];
  /** The current interaction mode. See ChatInteractionMode for semantics. */
  interactionMode: ChatInteractionMode;
  /** Update the interaction mode. Owned by DigitalTwinScreen. */
  setInteractionMode: (mode: ChatInteractionMode) => void;
  /**
   * The segment's icon URL, if one has been configured.
   * Used by SystemAuthor to render the segment avatar instead of the lightbulb
   * in Text/Voice mode. Undefined in non-DT contexts.
   */
  segmentIconUrl: string | undefined;
};

const defaultValue: AvatarPanelContextValue = {
  expanded: false,
  setExpanded: () => {},
  isDigitalTwin: false,
  isNewChatInteractionLayout: false,
  availableModes: ['video', 'voice', 'text'],
  defaultInteractionMode: 'video',
  interactionMode: 'video',
  setInteractionMode: () => {},
  segmentIconUrl: undefined,
};

export function reduceAvatarPanelContext(value: AvatarPanelContextValue): AvatarPanelContextValue {
  return {
    ...value,
    interactionMode: value.availableModes.includes(value.interactionMode) ? value.interactionMode : 'text',
    defaultInteractionMode: value.availableModes.includes(value.defaultInteractionMode)
      ? value.defaultInteractionMode
      : 'text',
  };
}

export const AvatarPanelContext = createContext<AvatarPanelContextValue>(defaultValue);

export function useAvatarPanel(): AvatarPanelContextValue {
  const context = useContext(AvatarPanelContext);

  if (context === undefined)
    throw new Error('useAvatarPanel must be used within an AvatarPanelContext.Provider');

  return context;
}
